/**
 * Bug-report module connection (portal flavor).
 *
 * TurboTwin ships ONE deployment-wide bug-report module: the house database
 * `turbotwin-bug-report`, co-hosted on the same cluster as the stamps. The
 * platform webclient, the Unreal client and the Twin Builder studio all
 * connect to that exact name; per-deployment access is gated server-side by
 * the module's WorkOS issuer registry, so there is no org or project in the
 * module name — it is a public platform contract, not an org-derived slug.
 *
 * Mirrors `studio/web/src/spacetime/bugReportModule.ts` (where the submit
 * recipe, the image-attachment recipe and the row projection carry vitest
 * coverage) with two portal-shaped differences: uri + token come from the
 * caller (the portal has no studio-style connection store), and the tiny
 * state store is a plain useSyncExternalStore feed because the portal
 * doesn't depend on zustand.
 * Connected lazily — the first time the report dialog opens — and outside
 * the SpacetimeDBMultiProvider tree: most sessions never file a report, and
 * a short-lived dialog socket needs none of the reconnect harness.
 */
import { useSyncExternalStore } from 'react';
import { DbConnection } from '@sdk/bugReport';
import {
  buildSystemInfoText,
  uploadReportAttachment,
  type BugReportConnectionLike,
  type FetchLike,
} from 'turbotwin-sdk/bug-report';

/** House module name — a platform contract, identical for every deployment. */
export const BUG_REPORT_MODULE =
  (import.meta.env.VITE_SPACETIME_BUGREPORT_MODULE as string | undefined) ||
  'turbotwin-bug-report';

export type ReportKindTag = 'Bug' | 'FeatureRequest' | 'Question' | 'Feedback';
export type BugReportStatusTag =
  | 'Open'
  | 'InProgress'
  | 'Resolved'
  | 'Wontfix'
  | 'Duplicate'
  | 'Withdrawn';

/** Reporter-facing views only; triage/admin tables stay the webclient's business. */
const SUBSCRIPTIONS = ['SELECT * FROM my_bug_report_user', 'SELECT * FROM my_bug_reports'];

const CONNECT_TIMEOUT_MS = 10_000;
const REVISION_DEBOUNCE_MS = 200;

export type BugReportStatus = 'idle' | 'connecting' | 'connected' | 'error';

export interface BugReportState {
  connection: DbConnection | null;
  status: BugReportStatus;
  lastError: string | null;
  /** Bumped when my_bug_reports changes so derived selectors re-run. */
  revision: number;
  /**
   * {@link tokenAuthKey} of the token the current build (connecting or
   * connected) was opened with. The dialog fetches a fresh org-scoped token
   * on every open, but connect used to early-return while connected — so
   * after an in-app org switch the socket kept the PREVIOUS org's token for
   * the life of the tab, and the module rejected submits with a permission
   * error (#99 cause 4: "works in one org, not the other"). The server judges
   * every reducer call by the JWT the socket was OPENED with, so tracking the
   * fingerprint lets connect rebuild exactly when the org or user changed,
   * while a same-org token refresh stays a cheap no-op.
   */
  authKey: string | null;
}

/**
 * Fingerprint of the claims the bug-report module judges a caller by: issuer +
 * subject (the module identity is blake3(iss|sub)) + `org_id` (the org the
 * report is filed under). A refreshed token for the same user and org keys the
 * same, so it reuses the socket; an org switch or another user keys
 * differently and forces a rebuild. An unparseable token keys as itself —
 * worst case an extra reconnect, never a stale-org socket.
 */
export function tokenAuthKey(token: string | null | undefined): string | null {
  const trimmed = token?.trim();
  if (!trimmed) return null;
  try {
    const payload: unknown = JSON.parse(
      atob(trimmed.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
    );
    if (typeof payload !== 'object' || payload === null) return trimmed;
    const claims = payload as Record<string, unknown>;
    return `${claims.iss ?? ''}|${claims.sub ?? ''}|${claims.org_id ?? ''}`;
  } catch {
    return trimmed;
  }
}

let state: BugReportState = {
  connection: null,
  status: 'idle',
  lastError: null,
  revision: 0,
  authKey: null,
};

/**
 * Monotonic build id. Every callback of a build checks it, so a superseded
 * socket's late onConnect / onConnectError / onDisconnect, its connect
 * timeout and its table listeners can't clobber the build that replaced it —
 * the SDK's `disconnect()` closes asynchronously, so those callbacks DO fire.
 */
let buildGeneration = 0;
/**
 * The handle `build()` returned for the current generation. Kept because
 * `connection` stays null until onConnect, and a superseded build must be
 * closed while it is still connecting too.
 */
let currentBuild: DbConnection | null = null;
const listeners = new Set<() => void>();

function setState(patch: Partial<BugReportState>): void {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useBugReportState(): BugReportState {
  return useSyncExternalStore(subscribe, () => state);
}

let revisionTimer: ReturnType<typeof setTimeout> | null = null;
function bumpRevision() {
  if (revisionTimer) return;
  revisionTimer = setTimeout(() => {
    revisionTimer = null;
    setState({ revision: state.revision + 1 });
  }, REVISION_DEBOUNCE_MS);
}

/** Close the current build (connecting or connected) and orphan its callbacks. */
function retireCurrentBuild(): void {
  buildGeneration++;
  const handles = new Set([currentBuild, state.connection]);
  currentBuild = null;
  if (revisionTimer) {
    clearTimeout(revisionTimer);
    revisionTimer = null;
  }
  for (const handle of handles) {
    try {
      handle?.disconnect();
    } catch {
      // stale handle — nothing left to close
    }
  }
}

export interface ReporterProfile {
  displayName: string;
  email: string;
}

export interface ConnectBugReportInput {
  uri: string;
  token: string | null;
  profile?: ReporterProfile;
}

/**
 * Connect to the house bug-report module with the caller's (fresh, org-scoped)
 * token. Fire-and-forget; safe to call again at any time:
 *  - same {@link tokenAuthKey} as the current connecting/connected build →
 *    no-op, so a token refresh never churns the socket;
 *  - different key → the current build is torn down and rebuilt with `token`,
 *    whether it is connected OR still connecting. The server reads the JWT
 *    the socket was opened with, so keeping the old socket would file every
 *    report under the old org/user and fail with a permission error (#99
 *    cause 4).
 *
 * WorkOS tokens don't always carry name/email claims, so — exactly like the
 * platform webclient — the caller passes the profile it already has and we
 * send `update_user_profile` once per connection so commenters don't render
 * as `Identity(...)`.
 */
export function connectBugReport({ uri, token, profile }: ConnectBugReportInput): void {
  if (!uri) return;
  const incomingKey = tokenAuthKey(token);
  if (state.status === 'connecting' || state.status === 'connected') {
    if (state.authKey === incomingKey) return;
    console.log('[bug-report] token org/user changed since connect — rebuilding socket');
  }

  // Also closes a timed-out or failed build's socket before a retry.
  retireCurrentBuild();
  const gen = buildGeneration;
  setState({
    connection: null,
    status: 'connecting',
    lastError: null,
    revision: 0,
    authKey: incomingKey,
  });
  // Whether THIS build ever reached onConnect — see onDisconnect below.
  let everConnected = false;
  const timer = setTimeout(() => {
    if (gen === buildGeneration && state.status === 'connecting') {
      setState({ status: 'error', lastError: 'Timed out connecting to the bug-report service.' });
    }
  }, CONNECT_TIMEOUT_MS);
  const bumpIfCurrent = () => {
    if (gen === buildGeneration) bumpRevision();
  };

  try {
    const builder = DbConnection.builder()
      .withUri(uri)
      .withDatabaseName(BUG_REPORT_MODULE)
      .withCompression('gzip');
    if (token?.trim()) {
      builder.withToken(token.trim());
    }

    builder.onConnect((conn) => {
      clearTimeout(timer);
      if (gen !== buildGeneration) {
        // Superseded while connecting — close it rather than let it go live.
        try {
          conn.disconnect();
        } catch {
          /* best effort */
        }
        return;
      }
      everConnected = true;
      currentBuild = conn;
      conn
        .subscriptionBuilder()
        .onError((ctx) => {
          console.warn('[bug-report] subscription error:', (ctx as { event?: unknown })?.event ?? ctx);
        })
        .subscribe(SUBSCRIPTIONS);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const reports = (conn.db as any).my_bug_reports;
      reports?.onInsert?.(bumpIfCurrent);
      reports?.onUpdate?.(bumpIfCurrent);
      reports?.onDelete?.(bumpIfCurrent);

      if (profile?.displayName || profile?.email) {
        void Promise.resolve(
          conn.reducers.updateUserProfile({
            displayName: profile.displayName,
            email: profile.email,
          }),
        ).catch((error) => {
          console.warn('[bug-report] updateUserProfile failed:', error);
        });
      }

      setState({ connection: conn, status: 'connected', lastError: null, revision: 0 });
    });

    builder.onConnectError((_ctx, error) => {
      clearTimeout(timer);
      if (gen !== buildGeneration) return;
      const message = error instanceof Error ? error.message : String(error);
      console.warn('[bug-report] connection error:', message);
      setState({ status: 'error', lastError: message });
    });

    builder.onDisconnect((_ctx, error) => {
      clearTimeout(timer);
      if (gen !== buildGeneration) return;
      currentBuild = null;
      const message = error ? (error instanceof Error ? error.message : String(error)) : null;
      if (!everConnected) {
        // The SDK's ws.onclose emits 'disconnect' for EVERY close — including
        // right after onConnectError on a socket that never opened (a refused
        // connect), despite the builder docs. Going 'idle' here would hide
        // the failure and the dialog's Retry, so a build that never connected
        // stays 'error', keeping the reason onConnectError / the timeout set.
        setState({
          connection: null,
          status: 'error',
          lastError:
            state.lastError ?? message ?? 'The connection closed before it was established.',
        });
        return;
      }
      setState({ connection: null, status: 'idle', lastError: message });
    });

    currentBuild = builder.build();
  } catch (error) {
    clearTimeout(timer);
    const message = error instanceof Error ? error.message : String(error);
    setState({ status: 'error', lastError: message });
  }
}

/** Allow the dialog's Retry button to clear a failed attempt and reconnect. */
export function retryBugReportConnection(input: ConnectBugReportInput): void {
  if (state.status === 'error') {
    setState({ status: 'idle', lastError: null });
  }
  connectBugReport(input);
}

/**
 * What the dialog shows for the connection, given the store and whether its
 * token request for this open/Retry is still in flight:
 *  - while the token is pending nothing is submittable, even over a live
 *    socket — that socket may still be the previous org's (the fresh token
 *    decides, and may rebuild it), and the SDK never settles a reducer call
 *    whose socket is closed under it, so a submit racing a rebuild would lock
 *    the dialog on "Submitting…";
 *  - an error (or a socket that dropped while open: 'idle' with a reason)
 *    shows the reason and Retry, once this attempt has finished.
 */
export function bugReportConnectionView({
  status,
  lastError,
  tokenPending,
}: {
  status: BugReportStatus;
  lastError: string | null;
  tokenPending: boolean;
}): { connecting: boolean; showError: boolean; ready: boolean } {
  return {
    connecting: tokenPending || status === 'connecting',
    showError: !tokenPending && (status === 'error' || (status === 'idle' && lastError !== null)),
    ready: !tokenPending && status === 'connected',
  };
}

/**
 * The dialog couldn't get a token to connect with (AuthKit refused or never
 * answered). Surfaced as a connection error so the dialog shows the reason
 * and its Retry instead of sitting idle with Submit disabled. A live socket
 * is closed too: without a token there is no telling whether it still
 * belongs to the session's current org, and filing under the wrong org is
 * exactly #99.
 */
export function reportBugReportTokenFailure(message: string): void {
  retireCurrentBuild();
  setState({ connection: null, status: 'error', lastError: message, authKey: null });
}

// ---------------------------------------------------------------------------
// submit
// ---------------------------------------------------------------------------

export interface SubmitReportInput {
  kind: ReportKindTag;
  summary: string;
  description: string;
  reporterDisplayName: string;
  reporterEmail: string;
  reporterOrgName: string;
  reporterProjectName: string;
  reporterProjectId: string;
  /** Intent flags on the report row; the bytes follow via {@link uploadReportExtras}. */
  includeScreenshot?: boolean;
  includeLog?: boolean;
}

/** How often the id-capture wait re-checks for the inserted row. */
const ID_POLL_MS = 50;

/**
 * How long to wait for the new report's id when image attachments are pending.
 * The default 50ms beat is fine when the id is cosmetic ("Report #7 submitted"),
 * but `register_image_attachment` cannot run without it, so the dialog asks for
 * a longer window whenever files are queued.
 */
export const IMAGE_ID_WAIT_MS = 2_000;

/**
 * Submit a report over an open connection, mirroring the platform webclient:
 * a temporary `my_bug_reports.onInsert` listener captures the new row's id
 * (matched by summary — the reducer returns nothing). `includeScreenshot` /
 * `includeLog` are intent flags only; the bytes go up afterwards through
 * {@link uploadReportExtras}, which needs the id.
 *
 * `waitForIdMs` bounds how long we poll for the insert after the reducer
 * settles: the historical single 50ms beat by default, {@link IMAGE_ID_WAIT_MS}
 * when the caller needs the id for image uploads.
 */
async function submitViaConnection(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  conn: any,
  input: SubmitReportInput,
  systemInfo: string,
  waitForIdMs = ID_POLL_MS,
): Promise<bigint | null> {
  let newId: bigint | null = null;
  const summary = input.summary.trim();
  const capture = (_ctx: unknown, row: { id: bigint; summary: string }) => {
    if (row.summary === summary) newId = row.id;
  };
  try {
    conn.db.my_bug_reports.onInsert?.(capture);
  } catch {
    // id capture is best-effort
  }
  try {
    await conn.reducers.submitBugReport({
      kind: { tag: input.kind },
      summary,
      description: input.description.trim(),
      systemInfo,
      includeScreenshot: input.includeScreenshot === true,
      includeLog: input.includeLog === true,
      reporterDisplayName: input.reporterDisplayName,
      reporterEmail: input.reporterEmail,
      reporterOrgName: input.reporterOrgName,
      reporterProjectName: input.reporterProjectName,
      reporterProjectId: input.reporterProjectId,
    });
    // Give the subscription a beat (or, with attachments pending, up to
    // `waitForIdMs`) to deliver the insert, then return whatever we captured.
    const deadline = Date.now() + waitForIdMs;
    do {
      await new Promise((resolve) => setTimeout(resolve, ID_POLL_MS));
      if (newId !== null) return newId;
    } while (Date.now() < deadline);
    return newId;
  } finally {
    try {
      conn.db.my_bug_reports.removeOnInsert?.(capture);
    } catch {
      // ignore
    }
  }
}

/** Submit on the module connection. Rejects when the module isn't connected. */
export async function submitBugReport(
  input: SubmitReportInput,
  waitForIdMs?: number,
): Promise<bigint | null> {
  const { connection, status } = state;
  if (!connection || status !== 'connected') {
    throw new Error('Not connected to the bug-report service yet.');
  }
  return submitViaConnection(connection, input, buildSystemInfoText(), waitForIdMs);
}

// ---------------------------------------------------------------------------
// screenshot + session log
// ---------------------------------------------------------------------------

export interface ReportExtras {
  /** One-frame tab capture (PNG), from the SDK's `captureTabScreenshot`. */
  screenshot?: Blob | null;
  /** The session.log body, from the SDK's `getSessionLogText`. */
  log?: Blob | null;
}

export interface ExtraUploadResult {
  kind: 'Screenshot' | 'Log';
  ok: boolean;
  error?: string;
}

export interface UploadExtrasOptions {
  /** Injected for tests; the SDK wraps global fetch by default. */
  fetchImpl?: FetchLike;
  /** Called before each upload starts, with a label for the dialog's status line. */
  onProgress?: (label: string) => void;
}

/**
 * Upload the screenshot and/or session log against an already-submitted
 * report, each through the SDK's presign -> PUT -> register recipe. Like the
 * images, one failing must not abort the other and never fails the submit:
 * every item gets a per-item result. Must run on the same connection that
 * submitted the report (the register reducer is reporter-only).
 */
export async function uploadExtrasViaConnection(
  conn: BugReportConnectionLike,
  reportId: bigint,
  extras: ReportExtras,
  options: UploadExtrasOptions = {},
): Promise<ExtraUploadResult[]> {
  const queue: Array<{ kind: 'Screenshot' | 'Log'; blob: Blob; contentType: string; label: string }> = [];
  if (extras.screenshot) {
    queue.push({ kind: 'Screenshot', blob: extras.screenshot, contentType: 'image/png', label: 'Uploading screenshot…' });
  }
  if (extras.log) {
    queue.push({ kind: 'Log', blob: extras.log, contentType: 'text/plain', label: 'Uploading session log…' });
  }
  const results: ExtraUploadResult[] = [];
  for (const item of queue) {
    options.onProgress?.(item.label);
    try {
      await uploadReportAttachment(conn, reportId, item.kind, item.blob, item.contentType, {
        fetchImpl: options.fetchImpl,
      });
      results.push({ kind: item.kind, ok: true });
    } catch (error) {
      results.push({
        kind: item.kind,
        ok: false,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }
  return results;
}

/** Upload on the module connection. Rejects when the module isn't connected. */
export async function uploadReportExtras(
  reportId: bigint,
  extras: ReportExtras,
  options: UploadExtrasOptions = {},
): Promise<ExtraUploadResult[]> {
  const { connection, status } = state;
  if (!connection || status !== 'connected') {
    throw new Error('Not connected to the bug-report service yet.');
  }
  return uploadExtrasViaConnection(connection as unknown as BugReportConnectionLike, reportId, extras, options);
}

// ---------------------------------------------------------------------------
// image attachments
// ---------------------------------------------------------------------------

/** Server-side cap on user-attached images per report (MAX_USER_IMAGES). */
export const MAX_REPORT_IMAGES = 5;
/** Server rejects size 0 and anything over 15MB. */
export const MAX_IMAGE_BYTES = 15 * 1024 * 1024;

/**
 * Extension allowlist and PUT Content-Type map — the same five extensions the
 * module validates (lowercase-compared). Keyed by extension, not by MIME, so a
 * mislabelled `file.type` can't sneak an unsupported format past us.
 */
export const IMAGE_CONTENT_TYPES: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  gif: 'image/gif',
};

/**
 * Lowercased extension from a file NAME (`Shot.PNG` → `png`), or null when the
 * name has no dot / trailing dot. The server derives its filename the same way
 * (lowercases + validates), so this is the field to validate — never
 * `file.type`, which some OSes leave blank.
 */
export function imageExtension(name: string): string | null {
  const i = name.lastIndexOf('.');
  if (i <= 0 || i === name.length - 1) return null;
  return name.slice(i + 1).toLowerCase();
}

/** Content-Type for the blob PUT; `file.type` is only a fallback. */
export function imageContentType(ext: string, fallback?: string): string {
  return IMAGE_CONTENT_TYPES[ext] ?? (fallback || 'application/octet-stream');
}

/**
 * Client-side pre-validation mirroring the server's rules, so a bad pick fails
 * fast with a friendly message instead of a procedure round-trip.
 * Returns the error text, or null when the file is attachable.
 */
export function validateImageFile(file: { name: string; size: number }): string | null {
  const ext = imageExtension(file.name);
  if (!ext || !(ext in IMAGE_CONTENT_TYPES)) {
    return `${file.name}: not a supported image (png, jpg, jpeg, webp, gif).`;
  }
  if (file.size === 0) {
    return `${file.name}: file is empty.`;
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return `${file.name}: larger than 15MB.`;
  }
  return null;
}

export interface ImageUploadTarget {
  url: string;
  blobPath: string;
  filename: string;
}

/**
 * Parse the `request_image_upload_url` reply: a JSON string envelope
 * `{"success":true,"data":{"url","blob_path","filename","expires_in"}}` or
 * `{"success":false,"error":…}`. Procedures sometimes double-encode (the first
 * JSON.parse yields a string — parse again; same tolerance as the studio's
 * `assetUpload.parseUrlPayload`). Throws the server's `error` text on a
 * failure envelope, and a generic error for anything unrecognisable —
 * including a non-http(s) `url`, which would otherwise resolve relative to the
 * dev server and 200 with index.html.
 */
export function parseImageUploadEnvelope(raw: unknown): ImageUploadTarget {
  let parsed: unknown = raw;
  try {
    if (typeof parsed === 'string') parsed = JSON.parse(parsed);
    if (typeof parsed === 'string') parsed = JSON.parse(parsed); // double-encoded
  } catch {
    throw new Error('Unexpected reply from the bug-report service.');
  }
  if (typeof parsed !== 'object' || parsed === null) {
    throw new Error('Unexpected reply from the bug-report service.');
  }
  const envelope = parsed as { success?: unknown; error?: unknown; data?: unknown };
  if (envelope.success !== true) {
    if (typeof envelope.error === 'string' && envelope.error) {
      throw new Error(envelope.error);
    }
    // Only an explicit success:false is a refusal; an object that carries
    // neither verdict nor error is just not the envelope.
    throw new Error(
      envelope.success === false
        ? 'The bug-report service refused the upload.'
        : 'Unexpected reply from the bug-report service.',
    );
  }
  const data = (envelope.data ?? {}) as Record<string, unknown>;
  const url = typeof data.url === 'string' ? data.url.trim() : '';
  const blobPath = typeof data.blob_path === 'string' ? data.blob_path : '';
  const filename = typeof data.filename === 'string' ? data.filename : '';
  if (!/^https?:\/\//i.test(url) || !blobPath || !filename) {
    throw new Error('Unexpected reply from the bug-report service.');
  }
  return { url, blobPath, filename };
}

export interface ImageUploadResult {
  /** The user's original file name — what the dialog shows on failure. */
  filename: string;
  ok: boolean;
  error?: string;
}

export interface UploadImagesOptions {
  /** Injected for tests; wraps global fetch by default (keeps `this` = globalThis). */
  fetchImpl?: (url: string, init: RequestInit) => Promise<{ ok: boolean; status: number }>;
  /** Called before each file starts, 1-based — drives "Uploading image 2 of 3…". */
  onProgress?: (current: number, total: number) => void;
}

/**
 * Upload user-picked images against an already-submitted report, one at a
 * time: `request_image_upload_url` (1-BASED index) → HTTP PUT of the bytes →
 * `register_image_attachment` echoing `blob_path` VERBATIM (the server
 * re-derives the canonical key and rejects anything else). One file failing
 * must not abort the rest — the report itself is already filed — so every
 * file gets a per-file result instead of a thrown error. Must run on the same
 * connection/identity that submitted the report (the reducer is
 * reporter-only). The studio twin exercises this recipe under vitest.
 */
export async function uploadImagesViaConnection(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  conn: any,
  reportId: bigint,
  files: File[],
  options: UploadImagesOptions = {},
): Promise<ImageUploadResult[]> {
  const fetchImpl = options.fetchImpl ?? ((url, init) => fetch(url, init));
  const results: ImageUploadResult[] = [];
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    options.onProgress?.(i + 1, files.length);
    try {
      const ext = imageExtension(file.name);
      if (!ext || !(ext in IMAGE_CONTENT_TYPES)) {
        throw new Error('not a supported image type');
      }
      const target = parseImageUploadEnvelope(
        await conn.procedures.requestImageUploadUrl({
          bugReportId: reportId,
          imageIndex: i + 1,
          ext,
        }),
      );
      const res = await fetchImpl(target.url, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': imageContentType(ext, file.type),
        },
        body: file,
      });
      if (!res.ok) throw new Error(`upload failed: HTTP ${res.status}`);
      await conn.reducers.registerImageAttachment({
        bugReportId: reportId,
        filename: target.filename,
        blobPath: target.blobPath,
        size: file.size,
      });
      results.push({ filename: file.name, ok: true });
    } catch (error) {
      results.push({
        filename: file.name,
        ok: false,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }
  return results;
}

/** Upload on the module connection. Rejects when the module isn't connected. */
export async function uploadReportImages(
  reportId: bigint,
  files: File[],
  options: UploadImagesOptions = {},
): Promise<ImageUploadResult[]> {
  const { connection, status } = state;
  if (!connection || status !== 'connected') {
    throw new Error('Not connected to the bug-report service yet.');
  }
  return uploadImagesViaConnection(connection, reportId, files, options);
}

